import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import {
  supabase,
} from "@/lib/supabase";
import {
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Trash2,
  FileText,
  Share2,
  Check,
  X,
  Download,
  Printer,
  Filter,
  CheckCircle2,
  XCircle,
  Clipboard,
  Clock,
  PackageCheck,
  Loader2,
  Receipt
} from "lucide-react";
import { useAuth } from "@/components/providers/AuthProvider";
import { Link } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { generateReceiptPDF } from "@/lib/receiptPdfGenerator";
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';

interface Product {
  id: string;
  name: string;
  code: string;
  unit: string;
  quantity: number;
  min_quantity: number;
}

interface OrderItem {
  id: string;
  quantity: number;
  products: Product;
  product_id: string;
}

interface Department {
  id: string;
  name: string;
  acronym: string;
}

interface Construction {
  id: string;
  name: string;
  address: string;
  status: string;
}

interface Order {
  id: string;
  order_number: number;
  responsible_name: string;
  construction_name: string;
  status: string;
  created_at: string;
  user_id: string;
  department: Department;
  order_items: OrderItem[];
  reject_reason?: string;
}

interface RawOrder {
  id: string;
  created_at: string;
  status: string;
  responsible_name?: string;
  construction_name?: string;
  construction?: Construction;
  department?: Department;
  order_items?: OrderItem[];
}

const OrdersManagement = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    completed: 0
  });
  // Função para obter a data atual no formato YYYY-MM-DD
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [filters, setFilters] = useState({
    status: "all",
    dateRange: {
      start: getCurrentDate(),
      end: getCurrentDate()
    },
    department: "all",
    orderNumber: ""
  });
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [orderToReject, setOrderToReject] = useState<Order | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);

  useEffect(() => {
    console.log('OrdersManagement montado, iniciando carregamento...');
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    console.log('Carregando dados iniciais...');
    
    try {
      await checkAdminStatus();
      await fetchDepartments();
      await fetchOrders();
      console.log('Dados iniciais carregados com sucesso');
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
    }
  };

  // Função para buscar departamentos
  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('id, name, acronym')
        .order('name');

      if (error) throw error;
      setDepartments(data || []);
    } catch (error) {
      console.error('Erro ao buscar departamentos:', error);
    }
  };

  // Função para verificar perfil de admin
  const checkAdminStatus = async (userId: string | undefined = undefined) => {
    // Por enquanto, vamos considerar todos como admin
    setIsAdmin(true);
    return true;
  };

  // Fetch orders
  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      console.log('Iniciando busca de pedidos...');
      
      // 1. Buscar pedidos com contagem total
      let query = supabase
        .from('orders')
        .select(`
          id,
          order_number,
          responsible_name,
          construction_name,
          status,
          created_at,
          user_id,
          reject_reason,
          departments (
            id,
            name,
            acronym
          ),
          order_items (
            id,
            quantity,
            products (
              id,
              name,
              code,
              unit
            )
          )
        `);
      
      // Aplicar filtro de status se não for "all"
      if (filters.status !== "all") {
        query = query.eq('status', filters.status);
      }
      
      // Aplicar filtro de número de pedido se existir
      if (filters.orderNumber) {
        query = query.ilike('order_number', `%${filters.orderNumber}%`);
      }
      
      // Aplicar filtro de data se ambas as datas estiverem preenchidas
      if (filters.dateRange.start && filters.dateRange.end) {
        const startDate = new Date(filters.dateRange.start + 'T00:00:00');
        const endDate = new Date(filters.dateRange.end + 'T23:59:59');
        
        console.log('Filtro de data aplicado:', {
          dataInicial: startDate.toISOString(),
          dataFinal: endDate.toISOString(),
          formatoDataInicial: filters.dateRange.start
        });
        
        query = query
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());
      }

      // Aplicar filtro de departamento
      if (filters.department !== "all") {
        query = query.eq('department_id', filters.department);
      }

      // Criar uma query para contar o total de registros com os mesmos filtros
      let countQuery = supabase
        .from('orders')
        .select('id', { count: 'exact' });

      // Aplicar os mesmos filtros à query de contagem
      if (filters.status !== "all") {
        countQuery = countQuery.eq('status', filters.status);
      }

      if (filters.orderNumber) {
        countQuery = countQuery.ilike('order_number', `%${filters.orderNumber}%`);
      }
      
      if (filters.department !== "all") {
        countQuery = countQuery.eq('department_id', filters.department);
      }

      if (filters.dateRange.start && filters.dateRange.end) {
        const startDate = new Date(filters.dateRange.start + 'T00:00:00');
        const endDate = new Date(filters.dateRange.end + 'T23:59:59');

        countQuery = countQuery
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());
      }

      // Executar a query principal com paginação
      const { data: ordersData, error: ordersError } = await query
        .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1)
        .order('created_at', { ascending: false });

      // Executar a query de contagem
      const { count: totalCount, error: countError } = await countQuery;

      if (ordersError || countError) {
        throw new Error(ordersError?.message || countError?.message);
      }

      // Formatar os dados dos pedidos
      const formattedOrders = ordersData?.map((order: any) => ({
        ...order,
        department: order.departments, // Renomear departments para department
        created_at: order.created_at, // Manter a data original para formatação posterior
        user_name: order.profiles?.full_name || 'Usuário não encontrado',
        user_email: order.profiles?.email || 'Email não disponível',
        department_name: order.departments?.name || 'Departamento não encontrado',
        department_acronym: order.departments?.acronym || 'N/A',
        total_items: order.order_items?.reduce((sum: number, item: any) => sum + item.quantity, 0) || 0
      })) || [];

      // Atualizar os estados - os dados já vêm filtrados e paginados do servidor
      console.log('Pedidos formatados:', formattedOrders);
      setOrders(formattedOrders);
      
      // Calcular o total de páginas com base na contagem total do servidor
      const calculatedTotalPages = Math.ceil(totalCount / itemsPerPage);
      console.log('Total de registros:', totalCount, 'Total de páginas:', calculatedTotalPages, 'Página atual:', currentPage);
      setTotalPages(calculatedTotalPages);

      // Calcular estatísticas aplicando os filtros ativos (exceto status)
      const stats = {
        total: totalCount || 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        completed: 0
      };

      // Executar queries para contar cada status com os filtros aplicados
      const statuses = ['pending', 'approved', 'rejected', 'completed'];
      const statusCounts = await Promise.all(statuses.map(async (status) => {
        let statsQuery = supabase
          .from('orders')
          .select('id', { count: 'exact' })
          .eq('status', status);
        
        // Aplicar filtros de departamento, data e número do pedido
        if (filters.department !== "all") {
          statsQuery = statsQuery.eq('department_id', filters.department);
        }
        
        if (filters.orderNumber) {
          statsQuery = statsQuery.ilike('order_number', `%${filters.orderNumber}%`);
        }
        
        if (filters.dateRange.start && filters.dateRange.end) {
          const startDate = new Date(filters.dateRange.start + 'T00:00:00');
          const endDate = new Date(filters.dateRange.end + 'T23:59:59');
          
          statsQuery = statsQuery
            .gte('created_at', startDate.toISOString())
            .lte('created_at', endDate.toISOString());
        }
        
        const { count } = await statsQuery;
        return { status, count: count || 0 };
      }));

      // Atualizar o objeto de estatísticas
      statusCounts.forEach(({ status, count }) => {
        stats[status as keyof typeof stats] = count;
      });

      console.log('Estatísticas calculadas:', stats);
      console.log('Status counts:', statusCounts);
      setStats(stats);
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os pedidos",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [filters.status, filters.orderNumber, filters.dateRange.start, filters.dateRange.end, filters.department, currentPage, itemsPerPage]);

  // Função para traduzir o status
  const translateStatus = (status: string): string => {
    const statusMap: { [key: string]: string } = {
      'pending': 'Pendente',
      'approved': 'Aprovado',
      'rejected': 'Rejeitado',
      'delivered': 'Entregue',
      'completed': 'Concluído'
    };
    return statusMap[status.toLowerCase()] || status;
  };

  // Função para obter as cores do status
  const getStatusStyle = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return {
          card: 'bg-yellow-50 border-yellow-200',
          text: 'text-yellow-700',
          badge: 'bg-yellow-100 text-yellow-800'
        };
      case 'approved':
        return {
          card: 'bg-green-50 border-green-200',
          text: 'text-green-700',
          badge: 'bg-green-100 text-green-800'
        };
      case 'rejected':
        return {
          card: 'bg-red-50 border-red-200',
          text: 'text-red-700',
          badge: 'bg-red-100 text-red-800'
        };
      case 'completed':
        return {
          card: 'bg-blue-50 border-blue-200',
          text: 'text-blue-700',
          badge: 'bg-blue-100 text-blue-800'
        };
      default:
        return {
          card: 'bg-gray-50 border-gray-200',
          text: 'text-gray-700',
          badge: 'bg-gray-100 text-gray-800'
        };
    }
  };

  const checkStockAvailability = async (orderId: number) => {
    try {
      console.log('\n=== Verificando disponibilidade de estoque ===');
      
      // 1. Buscar itens do pedido
      const { data: orderItems, error: orderError } = await supabase
        .from('order_items')
        .select(`
          id,
          quantity,
          product_id,
          products (
            id,
            code,
            name
          )
        `)
        .eq('order_id', orderId);

      if (orderError) {
        throw orderError;
      }

      console.log('Itens do pedido:', orderItems);

      // 2. Buscar estoque atual dos produtos
      const productIds = orderItems?.map(item => item.product_id) || [];
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select(`
          id,
          code,
          name,
          inventory!inner (
            quantity,
            department_id
          )
        `)
        .in('id', productIds);

      if (productsError) {
        throw productsError;
      }

      console.log('Produtos encontrados:', products);

      // 3. Criar mapa de produtos com suas quantidades
      const productsMap = new Map(
        products?.map(p => [p.id, {
          ...p,
          quantity: p.inventory?.[0]?.quantity || 0
        }])
      );

      // 4. Verificar disponibilidade
      const insufficientItems = [];
      for (const item of orderItems || []) {
        const product = productsMap.get(item.product_id);
        const stockQuantity = Number(product?.quantity) || 0;
        
        console.log(`\nVerificando produto ${product?.name}:`, {
          pedido: item.quantity,
          estoque: stockQuantity
        });

        if (stockQuantity < item.quantity) {
          insufficientItems.push(
            `${product?.name} (Pedido: ${item.quantity}, Disponível: ${stockQuantity})`
          );
        }
      }

      return {
        available: insufficientItems.length === 0,
        insufficientItems
      };
    } catch (error) {
      console.error('Erro ao verificar estoque:', error);
      throw error;
    }
  };

  const handleApproveOrRejectOrder = async (orderId: number, action: 'approved' | 'rejected') => {
    try {
      if (action === 'rejected' && !rejectReason.trim()) {
        toast({
          title: "Erro",
          description: "É necessário informar o motivo da rejeição",
          variant: "destructive",
        });
        return;
      }

      if (action === 'approved') {
        // 1. Verificar estoque
        console.log('\n=== Iniciando aprovação do pedido ===');
        const stockCheck = await checkStockAvailability(orderId);
        
        if (!stockCheck.available) {
          console.log('Estoque insuficiente:', stockCheck.insufficientItems);
          toast({
            title: "Erro - Estoque Insuficiente",
            description: `Não é possível aprovar o pedido. Os seguintes itens não têm estoque suficiente:\n${stockCheck.insufficientItems.join('\n')}`,
            variant: "destructive",
          });
          return;
        }

        // 2. Buscar itens do pedido
        const { data: orderItems, error: itemsError } = await supabase
          .from('order_items')
          .select(`
            id,
            quantity,
            product_id
          `)
          .eq('order_id', orderId);

        if (itemsError) {
          console.error('Erro ao buscar itens:', itemsError);
          throw itemsError;
        }

        // 3. Atualizar estoque de cada produto
        for (const item of orderItems || []) {
          // Buscar produto atual
          const { data: product, error: productError } = await supabase
            .from('products')
            .select(`
              id,
              name,
              inventory!inner (
                id,
                quantity,
                department_id
              )
            `)
            .eq('id', item.product_id)
            .single();

          if (productError) {
            console.error('Erro ao buscar produto:', productError);
            throw productError;
          }

          const currentQuantity = Number(product.inventory?.[0]?.quantity) || 0;
          const newQuantity = currentQuantity - Number(item.quantity);

          console.log(`Atualizando produto ${product.name}:`, {
            quantidade_atual: currentQuantity,
            quantidade_pedido: item.quantity,
            nova_quantidade: newQuantity
          });

          // Atualizar quantidade no inventário
          const { error: updateError } = await supabase
            .from('inventory')
            .update({ quantity: newQuantity })
            .eq('id', product.inventory[0].id);

          if (updateError) {
            console.error('Erro ao atualizar inventário:', updateError);
            throw updateError;
          }
        }
      }

      // 4. Atualizar status do pedido
      const { error: updateError } = await supabase
        .from('orders')
        .update({ 
          status: action === 'approved' ? 'approved' : 'rejected',
          ...(action === 'rejected' ? { reject_reason: rejectReason } : {})
        })
        .eq('id', orderId);

      if (updateError) throw updateError;

      // 5. Atualizar interface
      await fetchOrders();
      
      toast({
        title: "Sucesso",
        description: action === 'approved' 
          ? "Pedido aprovado com sucesso!"
          : "Pedido rejeitado com sucesso!",
      });

      // 6. Limpar estado
      if (action === 'rejected') {
        setOrderToReject(null);
        setRejectReason('');
      }
    } catch (error) {
      console.error('Erro ao processar pedido:', error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao processar o pedido",
        variant: "destructive",
      });
    }
  };

  const openRejectionModal = (orderId: string) => {
    // setSelectedOrderId(orderId);
    // setIsRejectionModalOpen(true);
  };

  const handleShareWhatsApp = (order: any) => {
    try {
      const message = 
        `🏢 *GestockPro - Detalhes do Pedido* 📦\n\n` +
        `📝 *Nº do Pedido:* ${order.order_number}\n` +
        `👤 *Responsável:* ${order.responsible_name}\n` +
        `🏢 *Departamento:* ${order.department?.name || 'Departamento não encontrado'}\n` +
        `🏗️ *Obra:* ${order.construction_name || 'N/A'}\n` +
        `📦 *Produto:* ${order.order_items[0].product.name}\n` +
        `📊 *Quantidade:* ${order.order_items[0].quantity}\n` +
        `📅 *Data do Pedido:* ${new Date(order.created_at).toLocaleDateString('pt-BR')}\n` +
        `🔄 *Status:* ${order.status === "Pendente" ? "Pendente" : order.status === "Aprovado" ? "Aprovado" : "Rejeitado"}\n\n` +
        `📝 *Observações:*\n${order.observation || 'Nenhuma observação'}`;

      const whatsappUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Erro ao compartilhar via WhatsApp:', error);
      toast({
        title: "Erro",
        description: "Não foi possível compartilhar via WhatsApp",
        variant: "destructive",
      });
    }
  };

  const generatePDF = async (orderId: string) => {
    try {
      setIsLoading(true);
      
      // Buscar dados detalhados do pedido
      const { data: orderData, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          responsible_name,
          construction_name,
          status,
          created_at,
          user_id,
          department:departments (
            id,
            name,
            acronym
          ),
          order_items (
            id,
            quantity,
            product_id,
            products (
              id,
              name,
              code,
              unit
            )
          )
        `)
        .eq('id', orderId)
        .single();

      if (error) {
        console.error('Erro ao buscar dados do pedido:', error);
        throw error;
      }
      if (!orderData) throw new Error('Pedido não encontrado');

      const doc = new jsPDF();
      
      // Cores do tema
      const colors = {
        primary: [41, 128, 185] as [number, number, number],
        secondary: [52, 73, 94] as [number, number, number],
        background: [245, 247, 250] as [number, number, number],
        text: [44, 62, 80] as [number, number, number],
        success: [46, 204, 113] as [number, number, number]
      };
      
      // Configurar margens
      const margin = 20;
      let yPos = margin;
      
      // Adicionar cabeçalho com fundo
      doc.setFillColor(...colors.primary);
      doc.rect(0, 0, doc.internal.pageSize.width, 40, 'F');
      
      // Título em branco
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(24);
      doc.text("GestockPro", doc.internal.pageSize.width / 2, 20, { align: "center" });
      doc.setFontSize(14);
      doc.text("Detalhes do Pedido", doc.internal.pageSize.width / 2, 32, { align: "center" });
      
      // Resetar cor do texto
      doc.setTextColor(...colors.text);
      yPos = 60;

      // Box de informações principais
      doc.setFillColor(...colors.background);
      doc.roundedRect(margin, yPos - 10, doc.internal.pageSize.width - (2 * margin), 65, 3, 3, 'F');
      
      // Informações do pedido em duas colunas
      doc.setFontSize(12);
      const col1X = 30;
      const col2X = doc.internal.pageSize.width / 2 + 10;
      
      // Coluna 1
      doc.setFont("helvetica", "bold");
      doc.text(`Pedido #${orderData.order_number}`, col1X, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(`Data: ${new Date(orderData.created_at).toLocaleDateString('pt-BR')}`, col1X, yPos + 12);
      
      // Coluna 2
      doc.setFont("helvetica", "bold");
      doc.text("Status:", col2X, yPos);
      
      // Status com cor
      const statusText = translateStatus(orderData.status);
      const statusColor = orderData.status.toLowerCase() === 'approved' ? colors.success : colors.secondary;
      doc.setTextColor(...statusColor);
      doc.setFont("helvetica", "normal");
      doc.text(statusText, col2X + 30, yPos);
      
      // Resetar cor do texto
      doc.setTextColor(...colors.text);
      doc.text(`Responsável: ${orderData.responsible_name}`, col2X, yPos + 12);

      // Informações em linhas separadas
      doc.text(`Departamento: ${orderData.department?.name || 'N/A'}`, col1X, yPos + 32);
      doc.text(`Obra: ${orderData.construction_name || 'N/A'}`, col1X, yPos + 44);
      
      yPos += 70;

      // Tabela de itens
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("Itens do Pedido", margin, yPos);
      
      yPos += 10;

      if (orderData.order_items && orderData.order_items.length > 0) {
        // Configuração da tabela de itens
        autoTable(doc, {
          startY: yPos,
          head: [['Produto', 'Código', 'Quantidade', 'Unidade']],
          body: orderData.order_items.map(item => [
            item.products?.name || 'Produto não encontrado',
            item.products?.code || 'N/A',
            item.quantity.toString(),
            item.products?.unit || 'N/A'
          ]),
          theme: 'grid',
          headStyles: {
            fillColor: colors.primary,
            textColor: 255,
            fontSize: 12,
            fontStyle: 'bold',
            halign: 'center'
          },
          bodyStyles: {
            fontSize: 11,
            textColor: colors.text
          },
          alternateRowStyles: {
            fillColor: colors.background
          },
          columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 40, halign: 'center' },
            2: { cellWidth: 30, halign: 'center' },
            3: { cellWidth: 30, halign: 'center' }
          },
          margin: { left: margin, right: margin }
        });
      } else {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        doc.text("Nenhum item encontrado", margin, yPos + 10);
      }

      // Se o pedido estiver aprovado, adiciona o campo de recebimento
      if (orderData.status.toLowerCase() === 'approved') {
        const finalY = (doc as any).lastAutoTable.finalY || yPos + 20;
        const startY = finalY + 30;
        
        // Box de recebimento
        doc.setFillColor(...colors.background);
        doc.roundedRect(margin, startY - 10, doc.internal.pageSize.width - (2 * margin), 70, 3, 3, 'F');
        
        // Título do recebimento
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Comprovante de Recebimento", margin + 10, startY + 5);
        
        // Texto do recebimento
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text("Declaro que recebi os itens acima descritos em perfeitas condições.", margin + 10, startY + 20);
        
        // Linhas para assinatura
        const lineY = startY + 35;
        doc.setDrawColor(...colors.secondary);
        doc.line(margin + 10, lineY, doc.internal.pageSize.width - 30, lineY);
        doc.text("Nome e Assinatura do Recebedor", doc.internal.pageSize.width / 2, lineY + 5, { align: "center" });
        
        doc.line(margin + 10, lineY + 20, margin + 80, lineY + 20);
        doc.text("Data do Recebimento", margin + 45, lineY + 25, { align: "center" });
      }

      // Rodapé
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Linha do rodapé
        const footerY = doc.internal.pageSize.height - 20;
        doc.setDrawColor(...colors.primary);
        doc.setLineWidth(0.5);
        doc.line(margin, footerY, doc.internal.pageSize.width - margin, footerY);
        
        // Texto do rodapé
        doc.setFontSize(8);
        doc.setTextColor(...colors.secondary);
        doc.text(
          `Página ${i} de ${pageCount}`,
          doc.internal.pageSize.width / 2,
          footerY + 5,
          { align: "center" }
        );
        
        doc.text(
          `Gerado em: ${new Date().toLocaleDateString('pt-BR')}`,
          margin,
          footerY + 5
        );
        
        doc.text(
          "GestockPro - Sistema de Gestão de Estoque",
          doc.internal.pageSize.width - margin,
          footerY + 5,
          { align: "right" }
        );
      }

      // Salvar o PDF
      doc.save(`pedido-${orderData.order_number}.pdf`);
      
      toast({
        title: "PDF gerado com sucesso",
        description: "O arquivo foi baixado para o seu computador",
      });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar o PDF. Por favor, tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewOrder = async (order: Order) => {
    try {
      console.log('\n=== VISUALIZANDO PEDIDO ===');
      console.log('Pedido selecionado:', order);

      // 1. Buscar dados completos do pedido com itens e produtos
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          responsible_name,
          construction_name,
          status,
          created_at,
          user_id,
          reject_reason,
          departments (id, name, acronym),
          order_items (id, quantity, product_id, products (id, name, code, unit))
        `)
        .eq('id', order.id)
        .single();

      if (orderError) {
        console.error('Erro ao buscar pedido:', orderError);
        throw orderError;
      }

      if (!orderData) {
        throw new Error('Pedido não encontrado');
      }

      console.log('Dados do pedido encontrados:', orderData);

      // 2. Buscar estoque disponível para cada produto no departamento do pedido
      const productIds = orderData.order_items.map(item => item.product_id);
      const { data: inventory, error: inventoryError } = await supabase
        .from('inventory')
        .select('product_id, quantity')
        .in('product_id', productIds)
        .eq('department_id', orderData.departments.id);

      if (inventoryError) {
        console.error('Erro ao buscar inventário:', inventoryError);
        throw inventoryError;
      }

      // 3. Criar mapa de estoque para fácil acesso
      const inventoryMap = new Map();
      inventory?.forEach(item => {
        inventoryMap.set(item.product_id, Number(item.quantity) || 0);
      });

      // 4. Formatar itens com informações completas
      const formattedItems = orderData.order_items.map(item => ({
        id: item.id,
        quantity: item.quantity,
        product_id: item.product_id,
        products: {
          id: item.products?.id || '',
          name: item.products?.name || 'Produto não encontrado',
          code: item.products?.code || 'Sem código',
          unit: item.products?.unit || 'UN',
          quantity: inventoryMap.get(item.product_id) || 0
        }
      }));

      // 5. Formatar pedido final
      const formattedOrder = {
        ...order,
        order_items: formattedItems,
        department: orderData.departments,
        reject_reason: orderData.reject_reason
      };

      console.log('\nPedido formatado:', JSON.stringify(formattedOrder, null, 2));

      // 6. Atualizar estado e abrir dialog
      setSelectedOrder(formattedOrder);
      setIsViewDialogOpen(true);
    } catch (error) {
      console.error('Erro ao visualizar pedido:', error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao visualizar o pedido",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (orderToReject) {
      handleApproveOrRejectOrder(Number(orderToReject.id), 'rejected');
    }
  }, [orderToReject]);

  useEffect(() => {
    if (orderToDelete) {
      handleDeleteConfirm();
    }
  }, [orderToDelete]);

  const handleDeleteClick = (order: Order) => {
    setOrderToDelete(order);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!orderToDelete) return;

    try {
      setIsLoading(true);

      // Usar uma única transação para excluir o pedido e seus itens
      const { error } = await supabase.rpc('delete_order_with_items', {
        order_id: orderToDelete.id
      });

      if (error) {
        console.error('Erro ao excluir pedido:', error);
        throw error;
      }

      toast({
        title: "Sucesso",
        description: "Pedido excluído com sucesso!",
      });

      // Atualizar a lista de pedidos
      await fetchOrders();
    } catch (error) {
      console.error('Erro ao excluir pedido:', error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao excluir o pedido. Por favor, tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setOrderToDelete(null);
      setIsLoading(false);
    }
  };

  const handleRejectClick = (order: Order) => {
    setOrderToReject(order);
    setIsRejectDialogOpen(true);
  };

  const getStatusVariant = (status: string): "default" | "success" | "destructive" | "warning" => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'destructive';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Função para formatar a data
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMessage = (order: Order) => {
    const status = translateStatus(order.status);
    const date = new Date(order.created_at).toLocaleDateString('pt-BR');
    const items = order.order_items
      .map(item => `▫️ ${item.quantity}x ${item.product.unit} - ${item.product.name}`)
      .join('\n');

    return encodeURIComponent(
      `🏗️ *Pedido #${order.order_number}*\n\n` +
      `📅 Data: ${date}\n` +
      `👤 Responsável: ${order.responsible_name}\n` +
      `🏢 Departamento: ${order.department?.name}\n` +
      `🏗️ Obra: ${order.construction_name}\n` +
      `📊 Status: ${status}\n\n` +
      `📦 *Itens do Pedido:*\n${items}\n\n` +
      `Sistema GestockPro 🚀`
    );
  };

  const handleShare = (order: Order) => {
    const message = formatMessage(order);
    window.open(`https://web.whatsapp.com/send?text=${message}`, '_blank');
  };

  useEffect(() => {
    // Quando o filtro de departamento mudar, atualizamos a página para 1 e buscamos novamente
    if (filters.department !== "all") {
      setCurrentPage(1);
    }
    fetchOrders();
  }, [filters.department]);

  // Resetar filtros
  const resetFilters = () => {
    setFilters({
      status: "all",
      dateRange: {
        start: "",
        end: ""
      },
      department: "all",
      orderNumber: ""
    });
    setCurrentPage(1);
  };

  // Paginação - usamos diretamente os pedidos carregados do servidor
  console.log('Usando pedidos carregados do servidor:', orders);

  useEffect(() => {
    // Resetar para a primeira página quando os filtros mudarem
    setCurrentPage(1);
  }, [filters]);

  // Não precisamos recalcular o totalPages aqui, pois já é calculado no fetchOrders
  // com base na contagem total retornada pelo servidor

  const DeleteOrderDialog = ({
    isOpen,
    onClose,
    orderToDelete,
    onConfirmDelete,
  }: any) => (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
          <AlertDialogDescription>
            Tem certeza que deseja excluir o pedido #{orderToDelete?.order_number}?
            Esta ação não pode ser desfeita.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (orderToDelete) {
                onConfirmDelete(orderToDelete.id);
              }
              onClose();
            }}
            className="bg-red-500 hover:bg-red-600"
          >
            Excluir
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const RejectOrderDialog = ({
    isOpen,
    onClose,
    orderToReject,
    onConfirmReject,
  }: any) => (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Rejeitar Pedido</AlertDialogTitle>
          <AlertDialogDescription>
            Por favor, informe o motivo da rejeição do pedido #{orderToReject?.order_number}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="reason">Motivo da Rejeição</Label>
            <Textarea
              id="reason"
              value={rejectReason}
              onChange={(e) => {
                console.log('Novo valor:', e.target.value);
                setRejectReason(e.target.value);
              }}
              placeholder="Digite o motivo da rejeição..."
              className="min-h-[100px]"
            />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (orderToReject) {
                onConfirmReject(orderToReject.id);
              }
              onClose();
            }}
            disabled={!rejectReason.trim()}
            className="bg-red-500 hover:bg-red-600"
          >
            Rejeitar Pedido
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // Estatísticas
  const orderStats = {
    total: orders.length,
    pending: orders.filter(order => order.status.toLowerCase() === 'pending').length,
    approved: orders.filter(order => order.status.toLowerCase() === 'approved').length,
    rejected: orders.filter(order => order.status.toLowerCase() === 'rejected').length,
    completed: orders.filter(order => order.status.toLowerCase() === 'completed').length
  };

  const handleExportPDF = async (orderId: string) => {
    try {
      setIsLoading(true);
      
      // Buscar dados detalhados do pedido
      const { data: orderData, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          responsible_name,
          construction_name,
          status,
          created_at,
          user_id,
          department (
            id,
            name,
            acronym
          ),
          order_items (
            id,
            quantity,
            product_id,
            products (
              id,
              name,
              code,
              unit
            )
          )
        `)
        .eq('id', orderId)
        .single();

      if (error) throw error;
      if (!orderData) throw new Error('Pedido não encontrado');

      const transformedOrder = {
        ...orderData,
        department: {
          id: orderData.department.id,
          name: orderData.department.name,
          acronym: orderData.department.acronym,
        },
        order_items: orderData.order_items.map((item: any) => ({
          id: item.id,
          quantity: item.quantity,
          product: {
            id: item.product.id,
            name: item.product.name,
            code: item.product.code,
            unit: item.product.unit,
          },
        })),
      };

      const doc = new jsPDF();
      
      // Cores do tema
      const colors = {
        primary: [41, 128, 185] as [number, number, number],
        secondary: [52, 73, 94] as [number, number, number],
        background: [245, 247, 250] as [number, number, number],
        text: [44, 62, 80] as [number, number, number],
        success: [46, 204, 113] as [number, number, number]
      };
      
      // Configurar margens
      const margin = 20;
      let yPos = margin;
      
      // Adicionar cabeçalho com fundo
      doc.setFillColor(...colors.primary);
      doc.rect(0, 0, doc.internal.pageSize.width, 40, 'F');
      
      // Título em branco
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(24);
      doc.text("GestockPro", doc.internal.pageSize.width / 2, 20, { align: "center" });
      doc.setFontSize(14);
      doc.text("Detalhes do Pedido", doc.internal.pageSize.width / 2, 32, { align: "center" });
      
      // Resetar cor do texto
      doc.setTextColor(...colors.text);
      yPos = 60;

      // Box de informações principais
      doc.setFillColor(...colors.background);
      doc.roundedRect(margin, yPos - 10, doc.internal.pageSize.width - (2 * margin), 65, 3, 3, 'F');
      
      // Informações do pedido em duas colunas
      doc.setFontSize(12);
      const col1X = 30;
      const col2X = doc.internal.pageSize.width / 2 + 10;
      
      // Coluna 1
      doc.setFont("helvetica", "bold");
      doc.text(`Pedido #${transformedOrder.order_number}`, col1X, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(`Data: ${new Date(transformedOrder.created_at).toLocaleDateString('pt-BR')}`, col1X, yPos + 12);
      
      // Coluna 2
      doc.setFont("helvetica", "bold");
      doc.text("Status:", col2X, yPos);
      
      // Status com cor
      const statusText = translateStatus(transformedOrder.status);
      const statusColor = transformedOrder.status.toLowerCase() === 'approved' ? colors.success : colors.secondary;
      doc.setTextColor(...statusColor);
      doc.setFont("helvetica", "normal");
      doc.text(statusText, col2X + 30, yPos);
      
      // Resetar cor do texto
      doc.setTextColor(...colors.text);
      doc.text(`Responsável: ${transformedOrder.responsible_name}`, col2X, yPos + 12);

      // Informações em linhas separadas
      doc.text(`Departamento: ${transformedOrder.department?.name || 'N/A'}`, col1X, yPos + 32);
      doc.text(`Obra: ${transformedOrder.construction_name || 'N/A'}`, col1X, yPos + 44);
      
      yPos += 70;

      // Tabela de itens
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("Itens do Pedido", margin, yPos);
      
      yPos += 10;

      if (transformedOrder.order_items && transformedOrder.order_items.length > 0) {
        // Configuração da tabela de itens
        autoTable(doc, {
          startY: yPos,
          head: [['Produto', 'Código', 'Quantidade', 'Unidade']],
          body: transformedOrder.order_items.map(item => [
            item.products?.name || 'Produto não encontrado',
            item.products?.code || 'N/A',
            item.quantity.toString(),
            item.products?.unit || 'N/A'
          ]),
          theme: 'grid',
          headStyles: {
            fillColor: colors.primary,
            textColor: 255,
            fontSize: 12,
            fontStyle: 'bold',
            halign: 'center'
          },
          bodyStyles: {
            fontSize: 11,
            textColor: colors.text
          },
          alternateRowStyles: {
            fillColor: colors.background
          },
          columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 40, halign: 'center' },
            2: { cellWidth: 30, halign: 'center' },
            3: { cellWidth: 30, halign: 'center' }
          },
          margin: { left: margin, right: margin }
        });
      } else {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        doc.text("Nenhum item encontrado", margin, yPos + 10);
      }

      // Se o pedido estiver aprovado, adiciona o campo de recebimento
      if (transformedOrder.status.toLowerCase() === 'approved') {
        const finalY = (doc as any).lastAutoTable.finalY || yPos + 20;
        const startY = finalY + 30;
        
        // Box de recebimento
        doc.setFillColor(...colors.background);
        doc.roundedRect(margin, startY - 10, doc.internal.pageSize.width - (2 * margin), 70, 3, 3, 'F');
        
        // Título do recebimento
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Comprovante de Recebimento", margin + 10, startY + 5);
        
        // Texto do recebimento
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text("Declaro que recebi os itens acima descritos em perfeitas condições.", margin + 10, startY + 20);
        
        // Linhas para assinatura
        const lineY = startY + 35;
        doc.setDrawColor(...colors.secondary);
        doc.line(margin + 10, lineY, doc.internal.pageSize.width - 30, lineY);
        doc.text("Nome e Assinatura do Recebedor", doc.internal.pageSize.width / 2, lineY + 5, { align: "center" });
        
        doc.line(margin + 10, lineY + 20, margin + 80, lineY + 20);
        doc.text("Data do Recebimento", margin + 45, lineY + 25, { align: "center" });
      }

      // Rodapé
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Linha do rodapé
        const footerY = doc.internal.pageSize.height - 20;
        doc.setDrawColor(...colors.primary);
        doc.setLineWidth(0.5);
        doc.line(margin, footerY, doc.internal.pageSize.width - margin, footerY);
        
        // Texto do rodapé
        doc.setFontSize(8);
        doc.setTextColor(...colors.secondary);
        doc.text(
          `Página ${i} de ${pageCount}`,
          doc.internal.pageSize.width / 2,
          footerY + 5,
          { align: "center" }
        );
        
        doc.text(
          `Gerado em: ${formatDate(new Date().toISOString())}`,
          margin,
          footerY + 5
        );
        
        doc.text(
          "GestockPro - Sistema de Gestão de Estoque",
          doc.internal.pageSize.width - margin,
          footerY + 5,
          { align: "right" }
        );
      }

      // Salvar o PDF
      doc.save(`pedido-${transformedOrder.order_number}.pdf`);
      
      toast({
        title: "PDF Gerado",
        description: "O PDF foi gerado com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar o PDF",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareOrder = async (order: any) => {
    try {
      const text = `*Detalhes do Pedido #${order.order_number}*\n\n` +
        `Status: ${order.status}\n` +
        `Data: ${formatDate(order.created_at)}\n` +
        `Departamento: ${order.department?.name || 'N/A'}\n\n` +
        `*Itens:*\n${order.order_items?.map((item: any, index: number) => 
          `${index + 1}. ${item.products?.name || 'Produto não encontrado'} - Qtd: ${item.quantity}`
        ).join('\n')}`;

      // Codificar o texto para URL
      const encodedText = encodeURIComponent(text);
      
      // Abrir WhatsApp Web com a mensagem
      window.open(`https://wa.me/?text=${encodedText}`, '_blank');
      
      toast({
        title: "Link Gerado",
        description: "O link para compartilhamento foi gerado!",
      });
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar o link de compartilhamento",
        variant: "destructive",
      });
    }
  };

  const handleDownloadReceipt = (order: Order) => {
    try {
      const doc = generateReceiptPDF(order);
      doc.save(`comprovante-pedido-${order.order_number}.pdf`);
      
      toast({
        title: "Sucesso",
        description: "Comprovante gerado com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao gerar comprovante:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar o comprovante",
        variant: "destructive",
      });
    }
  };

  const renderOrderDetails = (order: Order) => {
    if (!order || !order.order_items) {
      return <div>Carregando detalhes do pedido...</div>;
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="grid grid-cols-2 gap-4 flex-1">
            <div>
              <h3 className="font-semibold">Número do Pedido</h3>
              <p 
                className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
                onClick={() => navigate(`/orders/details/${order.id}`)}
              >
                #{order.order_number}
              </p>
            </div>
            <div>
              <h3 className="font-semibold">Status</h3>
              <Badge className={getStatusStyle(order.status).badge}>
                {translateStatus(order.status)}
              </Badge>
            </div>
          </div>
          {order.status === 'approved' && (
            <Button variant="outline" onClick={() => handleDownloadReceipt(order)}>
              <Receipt className="w-4 h-4 mr-2" />
              Gerar Comprovante
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="font-semibold">Responsável</h3>
            <p>{order.responsible_name}</p>
          </div>
          <div>
            <h3 className="font-semibold">Secretaria</h3>
            <p>{order.department?.name || 'Não informado'}</p>
          </div>
          <div>
            <h3 className="font-semibold">Obra</h3>
            <p>{order.construction_name}</p>
          </div>
          <div>
            <h3 className="font-semibold">Data de Criação</h3>
            <p>{formatDate(order.created_at)}</p>
          </div>
          {order.status === 'rejected' && order.reject_reason && (
            <div className="col-span-2">
              <h3 className="font-semibold">Motivo da Rejeição</h3>
              <p>{order.reject_reason}</p>
            </div>
          )}
        </div>

        <div>
          <h3 className="font-semibold mb-2">Itens do Pedido</h3>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>Quantidade</TableHead>
                  <TableHead>Unidade</TableHead>
                  <TableHead>Estoque Disponível</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {order.order_items && order.order_items.map((item) => {
                  const stockQuantity = Number(item.products?.quantity) || 0;
                  return (
                    <TableRow key={item.id}>
                      <TableCell>{item.products?.code || 'N/A'}</TableCell>
                      <TableCell>{item.products?.name || 'N/A'}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.products?.unit || 'N/A'}</TableCell>
                      <TableCell>{stockQuantity}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto py-6">
      {/* Cabeçalho */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">Gerenciamento de Pedidos</h1>
          <p className="text-muted-foreground">Gerencie todos os pedidos do sistema</p>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid gap-4 md:grid-cols-5">
        {/* Card - Total de Pedidos */}
        <Card className={cn("bg-white")}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Pedidos</CardTitle>
            <Clipboard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        {/* Card - Pedidos Pendentes */}
        <Card className={cn("bg-yellow-50 border-yellow-200")}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-yellow-700">Pendentes</CardTitle>
            <Clock className="h-4 w-4 text-yellow-700" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
          </CardContent>
        </Card>

        {/* Card - Pedidos Aprovados */}
        <Card className={cn("bg-green-50 border-green-200")}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-green-700">Aprovados</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-700" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{stats.approved}</div>
          </CardContent>
        </Card>

        {/* Card - Pedidos Rejeitados */}
        <Card className={cn("bg-red-50 border-red-200")}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-red-700">Rejeitados</CardTitle>
            <XCircle className="h-4 w-4 text-red-700" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{stats.rejected}</div>
          </CardContent>
        </Card>

        {/* Card - Pedidos Concluídos */}
        <Card className={cn("bg-blue-50 border-blue-200")}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-700">Concluídos</CardTitle>
            <PackageCheck className="h-4 w-4 text-blue-700" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{stats.completed}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Número do Pedido */}
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="orderNumber">Número do Pedido</Label>
              <Input
                id="orderNumber"
                placeholder="Digite o número"
                value={filters.orderNumber}
                onChange={(e) => setFilters(prev => ({ ...prev, orderNumber: e.target.value }))}
              />
            </div>

            {/* Status */}
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="status">Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) =>
                  setFilters({ ...filters, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="approved">Aprovado</SelectItem>
                  <SelectItem value="completed">Concluído</SelectItem>
                  <SelectItem value="rejected">Rejeitado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Secretaria */}
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="department">Secretaria</Label>
              <Select
                value={filters.department}
                onValueChange={(value) =>
                  setFilters({ ...filters, department: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id.toString()}>
                      {dept.acronym || dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Data */}
            <div className="flex flex-col space-y-1.5">
              <Label>Data Inicial</Label>
              <Input
                type="date"
                value={filters.dateRange.start}
                onChange={(e) =>
                  setFilters(prev => ({ 
                    ...prev, 
                    dateRange: { ...prev.dateRange, start: e.target.value } 
                  }))
                }
              />
            </div>
            <div className="flex flex-col space-y-1.5">
              <Label>Data Final</Label>
              <Input
                type="date"
                value={filters.dateRange.end}
                onChange={(e) =>
                  setFilters(prev => ({ 
                    ...prev, 
                    dateRange: { ...prev.dateRange, end: e.target.value } 
                  }))
                }
              />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button 
              variant="outline" 
              onClick={resetFilters}
              className="flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-refresh-ccw"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 21h5v-5"/></svg>
              Limpar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Número</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Departamento</TableHead>
                  <TableHead>Obra</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10">
                      <div className="flex justify-center">
                        <Loader2 className="h-6 w-6 animate-spin" />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10">
                      Nenhum pedido encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>#{order.order_number}</TableCell>
                      <TableCell>{formatDate(order.created_at)}</TableCell>
                      <TableCell>{order.department?.acronym || order.department?.name}</TableCell>
                      <TableCell>{order.construction_name}</TableCell>
                      <TableCell>
                        <Badge className={getStatusStyle(order.status).badge}>
                          {translateStatus(order.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewOrder(order)}
                            title="Ver detalhes"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {order.status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleApproveOrRejectOrder(order.id, 'approved')}
                                className="text-green-500 hover:text-green-600"
                                title="Aprovar pedido"
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRejectClick(order)}
                                className="text-red-500 hover:text-red-600"
                                title="Rejeitar pedido"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleShare(order)}
                            title="Compartilhar"
                          >
                            <Share2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        <CardFooter className="flex items-center justify-between p-4">
          <div className="flex-1 text-sm text-muted-foreground">
            Mostrando {orders.length} de {stats.total} pedidos
          </div>
          <div className="flex items-center space-x-6 lg:space-x-8">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium">Itens por página</p>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => setItemsPerPage(Number(value))}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={itemsPerPage.toString()} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                    <SelectItem key={pageSize} value={pageSize.toString()}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex w-[100px] items-center justify-center text-sm font-medium">
              Página {currentPage} de {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                <span className="sr-only">Primeira página</span>
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                <span className="sr-only">Página anterior</span>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages || orders.length === 0}
              >
                <span className="sr-only">Próxima página</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => setCurrentPage(Math.max(1, totalPages))}
                disabled={currentPage === totalPages}
              >
                <span className="sr-only">Última página</span>
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardFooter>
      </Card>
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Pedido</DialogTitle>
            <DialogDescription>
              Visualize as informações completas do pedido e seus itens
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && renderOrderDetails(selectedOrder)}
        </DialogContent>
      </Dialog>
      <Dialog open={!!orderToReject} onOpenChange={() => setOrderToReject(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Pedido #{orderToReject?.order_number}</DialogTitle>
            <DialogDescription>
              Por favor, informe o motivo da rejeição do pedido.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="reason">Motivo da Rejeição</Label>
              <Textarea
                id="reason"
                value={rejectReason}
                onChange={(e) => {
                  console.log('Novo valor:', e.target.value);
                  setRejectReason(e.target.value);
                }}
                placeholder="Digite o motivo da rejeição..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setOrderToReject(null);
                setRejectReason("");
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (orderToReject) {
                  handleApproveOrRejectOrder(orderToReject.id, 'rejected');
                }
                setOrderToReject(null);
                setRejectReason("");
              }}
              disabled={!rejectReason.trim()}
            >
              Rejeitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DeleteOrderDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        orderToDelete={orderToDelete}
        onConfirmDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default OrdersManagement;